import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";
import { Button } from "@/components/ui/button";

interface ThemeToggleProps {
  /** "icon" renders a round icon-only button (for the top nav); default keeps the sidebar-styled full-width button. */
  variant?: "icon";
}

/**
 * Light or dark, and nothing else.
 *
 * The OS preference still decides where a first-time visitor lands —
 * `defaultTheme` is "system" (see App.tsx), so the first paint matches the
 * desktop and there is no flash of the wrong theme. But "System" is not a
 * third thing to click. It was a state the control had to explain, sitting in
 * a cycle of three where two clicks could leave you back where you started,
 * and it answered a question ("which is it right now?") that the button's own
 * icon already answers. The switch reads the RESOLVED theme and flips it, so
 * the first click from the system default goes straight to the opposite of
 * what is on screen — which is the only thing anyone wants from it.
 */
export function ThemeToggle({ variant }: ThemeToggleProps = {}) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  // next-themes cannot resolve the OS preference until it has run in the
  // browser, so rendering before mount would show the wrong icon.
  if (!mounted) return null;

  const isDark = resolvedTheme === "dark";
  // The icon shows the current theme, so the label has to carry the action —
  // otherwise a screen reader hears the state twice and never what the click does.
  const label = isDark ? "Dark theme. Switch to light." : "Light theme. Switch to dark.";
  const Icon = isDark ? Moon : Sun;
  const flip = () => setTheme(isDark ? "light" : "dark");

  if (variant === "icon") {
    return (
      <Button
        variant="ghost"
        size="icon"
        onClick={flip}
        aria-label={label}
        title={label}
        className="h-9 w-9 rounded-full border border-border bg-card text-muted-foreground"
      >
        <Icon className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={flip}
      aria-label={label}
      className="w-full justify-start gap-2 text-sidebar-foreground/70 hover:text-sidebar-foreground hover:bg-sidebar-accent"
    >
      <Icon className="h-4 w-4" />
      {isDark ? "Dark" : "Light"}
    </Button>
  );
}
